import { createInstance } from "i18next";
import { initReactI18next } from "react-i18next/initReactI18next";
import resourcesToBackend from "i18next-resources-to-backend";
import i18nConfig from "../../next-i18next.config";
import { getSiteDomain } from "@/utils/site";

const domainSlugFromHost = (host) => host.split(".")[0];

function isPlainObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function deepMerge(a, b) {
  const out = { ...(a || {}) };
  for (const [k, v] of Object.entries(b || {})) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMerge(out[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

const normalizeNsKey = (ns) => ns.replace(/-/g, "_");

export default async function initTranslations({
  locale,
  namespaces,
  i18nInstance = undefined,
  resources = undefined,
}) {
  i18nInstance = i18nInstance || createInstance();
  i18nInstance.use(initReactI18next);

  const domainFolder = getSiteDomain();
  const domainSlug = domainSlugFromHost(domainFolder);

  if (!resources) {
    i18nInstance.use(
      resourcesToBackend(async (language, namespace) => {
        const mod = await import(`@/locales/${language}/${namespace}.json`);
        const data = mod.default || mod;

        // If namespace has a dash (forgot-password), this must be converter to snake case (forgot_password)
        const nsKey = normalizeNsKey(namespace);
        const bucket = data?.[nsKey] ?? data?.[namespace];

        if (!bucket || !isPlainObject(bucket)) return {};

        const common = bucket.common || {};
        const domain = bucket[domainSlug] || {};
        return deepMerge(common, domain);
      })
    );
  }

  await i18nInstance.init({
    lng: locale,
    resources,
    fallbackLng: i18nConfig.defaultLocale,
    supportedLngs: i18nConfig.locales,
    defaultNS: namespaces[0],
    fallbackNS: namespaces[0],
    ns: namespaces,
    preload: resources ? [] : i18nConfig.locales,
  });

  return {
    i18n: i18nInstance,
    resources: i18nInstance.services.resourceStore.data,
    t: i18nInstance.t,
  };
}
